import { useMemo, useState } from "react";
import {
    ArrowLeft,
    ArrowRight,
    MessageCircle,
    Sparkles,
    BriefcaseBusiness,
    Palette,
    Rocket,
    Target
} from "lucide-react";
import {
    INDUSTRY_OPTIONS,
    getIndustryDefaults
} from "./generatorIndustryConfig";

const STYLE_OPTIONS = [
    {
        key: "Premium",
        label: "Premium",
        helper: "Trust-focused and polished"
    },
    {
        key: "Modern",
        label: "Modern",
        helper: "Clean startup-style layout"
    },
    {
        key: "Bold",
        label: "Bold",
        helper: "Strong visual energy"
    },
    {
        key: "Clean",
        label: "Clean",
        helper: "Simple and clear"
    },
    {
        key: "Luxury",
        label: "Luxury",
        helper: "Elegant and high-end"
    }
];

function valueOrDash(value) {
    return value && String(value).trim() ? value : "-";
}

function ChatBubble({ children, type = "ai" }) {
    return (
        <div className={`mgcf-bubble ${type}`}>
            {type === "ai" ? (
                <div className="mgcf-avatar">
                    <Sparkles size={15} />
                </div>
            ) : null}

            <div className="mgcf-bubble-card">
                {children}
            </div>
        </div>
    );
}

function OptionChips({ options, selected, onSelect }) {
    return (
        <div className="mgcf-chip-grid">
            {options.map((item) => {
                const option = typeof item === "string"
                    ? { key: item, label: item }
                    : item;

                const value = option.key || option.label;

                return (
                    <button
                        type="button"
                        key={value}
                        className={`mgcf-chip ${selected === value ? "active" : ""}`}
                        onClick={() => onSelect(value)}
                    >
                        <strong>{option.label}</strong>
                        {option.helper ? <span>{option.helper}</span> : null}
                    </button>
                );
            })}
        </div>
    );
}

export default function MobileGeneratorChatFlow({
    form,
    updateField,
    onBack,
    onGenerate,
    generating = false,
    canGenerate = true
}) {
    /*
      Always confirm industry after business name.
      This prevents description/CTA/business-model from silently changing vertical later.
    */
    const [step, setStep] = useState(() => {
        if (!form.businessName) return 0;
        return 1;
    });

    const [customValue, setCustomValue] = useState("");

    const industryDefaults = useMemo(() => {
        return getIndustryDefaults(form.industry);
    }, [form.industry]);

    const ctaOptions = industryDefaults.ctas || ["Contact Us"];
    const pages = industryDefaults.pages || ["Home", "About", "Services", "Contact"];

    const crmModules = useMemo(() => {
        const entity = String(form.primaryEntityType || industryDefaults.primaryEntityType || "").toLowerCase();

        if (entity.includes("program")) return ["Programs", "Trainers / Coaches", "Leads", "Testimonials"];
        if (entity.includes("listing")) return ["Listings", "Services", "Leads", "Testimonials"];
        if (entity.includes("product")) return ["Products", "Collections", "Leads", "Offers"];
        if (entity.includes("doctor")) return ["Doctors", "Services", "Appointments", "Leads"];
        if (entity.includes("feature")) return ["Features", "Pricing", "Leads", "FAQ"];

        return ["Services", "Leads", "Testimonials", "Settings"];
    }, [form.primaryEntityType, industryDefaults.primaryEntityType]);

    const applyIndustryLock = (industry) => {
        const defaults = getIndustryDefaults(industry);

        updateField("industry", industry);
        updateField("subIndustry", defaults.subIndustry);
        updateField("businessModel", defaults.businessModel);
        updateField("primaryWebsiteMode", defaults.primaryWebsiteMode);
        updateField("primaryEntityType", defaults.primaryEntityType);
        updateField("locationFocus", defaults.locationFocus);
        updateField("pages", defaults.pages);

        if (!form.callToActionType || !defaults.ctas.includes(form.callToActionType)) {
            updateField("callToActionType", defaults.ctas?.[0] || "Contact Us");
        }

        if (!form.primaryAudience || !defaults.audiences.includes(form.primaryAudience)) {
            updateField("primaryAudience", defaults.audiences?.[0] || "Local customers");
        }

        if (!form.coreServices) {
            updateField("coreServices", defaults.offerings?.[0] || "Services");
        }
    };

    const steps = [
        {
            key: "industry",
            icon: BriefcaseBusiness,
            title: form.industry
                ? `Generate this as ${form.industry}?`
                : "Choose your industry",
            subtitle: "This controls layouts, CRM modules, pages, and AI content.",
            value: form.industry,
            type: "chips",
            options: INDUSTRY_OPTIONS,
            apply: applyIndustryLock
        },
        {
            key: "callToActionType",
            icon: Target,
            title: "What should visitors do?",
            subtitle: "Choose the primary action for your website.",
            value: form.callToActionType,
            type: "chips",
            options: ctaOptions,
            apply: (value) => {
                updateField("callToActionType", value);
                updateField("goalOfWebsite", value);
            }
        },
        {
            key: "brandStyle",
            icon: Palette,
            title: "Choose your website style",
            subtitle: "Nexus will combine this with your industry.",
            value: form.brandStyle,
            type: "style",
            options: STYLE_OPTIONS,
            apply: (value) => {
                updateField("brandStyle", value);
                updateField("contentStyleHint", value.toLowerCase());
            }
        },
        {
            key: "summary",
            icon: Rocket,
            title: "Ready to generate",
            subtitle: "Your AI website plan is ready.",
            type: "summary"
        }
    ];

    const current = steps[step];
    const CurrentIcon = current.icon;
    const isSummary = current.type === "summary";
    const progress = Math.round(((step + 1) / steps.length) * 100);

    const canContinue = isSummary || Boolean(current.value && String(current.value).trim());

    const applyCustom = () => {
        const value = customValue.trim();
        if (!value) return;

        if (current.key === "coreServices") {
            const existing = form.coreServices || "";
            current.apply(existing ? `${existing}, ${value}` : value);
        } else {
            current.apply(value);
        }

        setCustomValue("");
    };

    const continueStep = () => {
        if (step < steps.length - 1) {
            setStep((prev) => prev + 1);
        }
    };

    const goBack = () => {
        if (step > 0) {
            setStep((prev) => prev - 1);
            return;
        }

        if (onBack) onBack();
    };

    const handleGenerate = () => {
        const lockedDefaults = getIndustryDefaults(form.industry || "Business");

        const finalPatch = {
            businessName: form.businessName || "My Business",
            industry: form.industry || "Business",
            subIndustry: form.subIndustry || lockedDefaults.subIndustry || form.industry || "Business",
            businessModel: form.businessModel || lockedDefaults.businessModel || "service",
            primaryWebsiteMode: form.primaryWebsiteMode || lockedDefaults.primaryWebsiteMode || "conversion-first",
            primaryEntityType: form.primaryEntityType || lockedDefaults.primaryEntityType || "services",
            locationFocus: form.locationFocus || lockedDefaults.locationFocus || "local",
            goalOfWebsite: form.goalOfWebsite || form.callToActionType || lockedDefaults.ctas?.[0] || "Generate Leads",
            callToActionType: form.callToActionType || lockedDefaults.ctas?.[0] || "Contact Us",
            brandStyle: form.brandStyle || "Premium",
            tone: form.tone || "Professional",
            primaryAudience: form.primaryAudience || lockedDefaults.audiences?.[0] || "Local customers",
            coreServices: form.coreServices || lockedDefaults.offerings?.[0] || "Services",
            pages: Array.isArray(lockedDefaults.pages) && lockedDefaults.pages.length > 0
                ? lockedDefaults.pages
                : ["Home", "About", "Services", "Contact"]
        };

        Object.entries(finalPatch).forEach(([key, value]) => {
            updateField(key, value);
        });

        if (onGenerate) {
            onGenerate(finalPatch);
        }
    };

    return (
        <main className="mgcf-page">
            <section className="mgcf-shell">
                <div className="mgcf-topbar">
                    <button
                        type="button"
                        className="mgcf-icon-btn"
                        onClick={goBack}
                        aria-label="Back"
                    >
                        <ArrowLeft size={18} />
                    </button>

                    <div className="mgcf-brand">
                        <div className="mgcf-brand-icon">
                            <MessageCircle size={17} />
                        </div>

                        <div>
                            <strong>Nexus AI Builder</strong>
                            <span>{progress}% complete</span>
                        </div>
                    </div>

                    <div className="mgcf-progress-pill">
                        {step + 1}/{steps.length}
                    </div>
                </div>

                <div className="mgcf-progress">
                    <span style={{ width: `${progress}%` }} />
                </div>

                <div className="mgcf-thread">
                    <ChatBubble>
                        <div className="mgcf-question-head">
                            <div className="mgcf-question-icon">
                                <CurrentIcon size={18} />
                            </div>

                            <div>
                                <h1>{current.title}</h1>
                                <p>{current.subtitle}</p>
                            </div>
                        </div>
                    </ChatBubble>

                    {current.value && !isSummary && current.type !== "description" ? (
                        <ChatBubble type="user">
                            <strong>{current.value}</strong>
                        </ChatBubble>
                    ) : null}

                    {current.type === "input" ? (
                        <div className="mgcf-answer-card">
                            <input
                                value={customValue || current.value || ""}
                                onChange={(e) => {
                                    setCustomValue(e.target.value);
                                    current.apply(e.target.value);
                                }}
                                placeholder={current.placeholder}
                                autoFocus
                            />
                        </div>
                    ) : null}

                    {current.type === "chips" ? (
                        <div className="mgcf-answer-card">
                            <OptionChips
                                options={current.options}
                                selected={current.value}
                                onSelect={(value) => current.apply(value)}
                            />
                        </div>
                    ) : null}

                    {current.type === "chipsWithCustom" ? (
                        <div className="mgcf-answer-card">
                            <OptionChips
                                options={current.options}
                                selected={current.value}
                                onSelect={(value) => {
                                    const existing = form.coreServices || "";
                                    const next = existing ? `${existing}, ${value}` : value;
                                    current.apply(next);
                                }}
                            />

                            <div className="mgcf-custom-row">
                                <input
                                    value={customValue}
                                    onChange={(e) => setCustomValue(e.target.value)}
                                    placeholder="Add custom offering"
                                />
                                <button type="button" onClick={applyCustom}>
                                    Add
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {current.type === "style" ? (
                        <div className="mgcf-answer-card">
                            <OptionChips
                                options={current.options}
                                selected={current.value}
                                onSelect={(value) => current.apply(value)}
                            />
                        </div>
                    ) : null}

                    {isSummary ? (
                        <div className="mgcf-summary-card mgcf-summary-card-compact">
                            <div className="mgcf-summary-head">
                                <div>
                                    <span>Final Plan</span>
                                    <h2>{valueOrDash(form.businessName)}</h2>
                                    <p>Your website preview is ready to generate.</p>
                                </div>

                                <div className="mgcf-summary-icon">
                                    <Rocket size={22} />
                                </div>
                            </div>

                            <div className="mgcf-summary-grid">
                                <div>
                                    <span>Industry</span>
                                    <strong>{valueOrDash(form.subIndustry || form.industry)}</strong>
                                </div>

                                <div>
                                    <span>CTA</span>
                                    <strong>{valueOrDash(form.callToActionType)}</strong>
                                </div>

                                <div>
                                    <span>Style</span>
                                    <strong>{valueOrDash(form.brandStyle || "Premium")}</strong>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="mgcf-bottom-bar">
                    <button
                        type="button"
                        className="mgcf-secondary-btn"
                        onClick={isSummary ? () => setStep(0) : continueStep}
                    >
                        {isSummary ? "Edit Plan" : "Skip"}
                    </button>

                    {!isSummary ? (
                        <button
                            type="button"
                            className="mgcf-primary-btn"
                            disabled={!canContinue}
                            onClick={continueStep}
                        >
                            Continue
                            <ArrowRight size={17} />
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="mgcf-primary-btn mgcf-generate-btn"
                            disabled={generating || !canGenerate}
                            onClick={handleGenerate}
                        >
                            {generating ? "Generating..." : "Generate Website"}
                            <Rocket size={17} />
                        </button>
                    )}
                </div>
            </section>
        </main>
    );
}